import { observer } from 'mobx-react';
import { lazy, Suspense, useId } from 'react';

import InputField from '../../../app/shared/components/Input/InputField';
import Label from '../../../app/shared/components/Label/Label';
import { ExportPricelistToPdfModalTranslation } from '../../localization/ModuleTranslation';
import Button from '../../ui/Button/Button';
import { Modal, ModalContent, ModalHeader, ModalHeading } from '../../ui/Modal/Modal';
import IncludeNetPriceCheckbox from '../IncludeNetPriceCheckbox/IncludeNetPriceCheckbox';
import css from './ExportPricelistToPdfModal.css';
import ExportPricelistToPdfModalContent from './ExportPricelistToPdfModalContent';
import ExportPricelistToPdfModalState from './ExportPricelistToPdfModalState';

/**
 * The renderer and the PDF viewer together outweigh the rest of the app, and are wanted on one modal
 * of one page, so they are fetched when a dealer asks to export rather than shipped with the bundle.
 *
 * The fonts load with the chunk because a document measures its own text while it renders. A face that
 * fails to load is left to fail that render, which the modal reports, rather than the lazy import.
 */
const ExportPricelistToPdfModalRenderer = lazy(() =>
  Promise.all([
    import(/* webpackChunkName: 'pricelists-pdf' */ './ExportPricelistToPdfModalRenderer'),
    import(/* webpackChunkName: 'pricelists-pdf' */ '../../tools/pdf/fonts')
      .then(({ loadFonts }) => loadFonts())
      .catch((): void => undefined),
  ]).then(([module]) => module),
);

interface ExportPricelistToPdfModalProps {
  state: ExportPricelistToPdfModalState;
  translation: ExportPricelistToPdfModalTranslation;
  trigger: React.ReactNode;
}

const ExportPricelistToPdfModal = observer(({ state, translation, trigger }: ExportPricelistToPdfModalProps) => {
  const titleId = useId();

  const handleTitleSubmit = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    state.changeTitle();
  };

  return (
    <Modal isOpen={state.visible} onOpenChange={state.toggle}>
      {trigger}
      <ModalContent>
        <ModalHeader closeLabel={translation.closeLabel}>
          <ModalHeading>{translation.heading}</ModalHeading>
        </ModalHeader>
        <form className={css.form} onSubmit={handleTitleSubmit} noValidate>
          <Label htmlFor={titleId}>{translation.titleInputLabel}</Label>
          <div className={css.formRow}>
            <InputField
              id={titleId}
              name="title"
              input={state.titleInput}
              placeholder={translation.titleInputPlaceholder}
              autoFocus={!state.title}
            />
            <Button type="submit" theme="primary" variant="outline" isDisabled={!state.canChangeTitle}>
              {translation.titleSubmitLabel}
            </Button>
          </div>
          <IncludeNetPriceCheckbox
            className={css.includeNetPrice}
            isSelected={state.includeNetPrice}
            isAvailable={state.canIncludeNetPrice}
            onChange={state.setIncludeNetPrice}
            translation={translation.includeNetPrice}
          />
        </form>
        {state.status === 'success' ? (
          <Suspense fallback={<ExportPricelistToPdfModalContent status="pending" translation={translation} />}>
            <ExportPricelistToPdfModalRenderer data={state.documentData} translation={translation} />
          </Suspense>
        ) : (
          <ExportPricelistToPdfModalContent status={state.status} translation={translation} />
        )}
      </ModalContent>
    </Modal>
  );
});

export default ExportPricelistToPdfModal;
